import { useState, useEffect, useRef } from "react";
import { Bug } from "lucide-react";

import { Switch } from "@/components/ui/switch";

export const SHOW_DEBUG_ITEMS_ENABLED = "showDebugItemsEnabled";

export const ToggleShowDebugItems = () => {
  const [showToggle, setShowToggle] = useState(false);

  // Initialize from localStorage instead of estimation prop
  const [isShowDebugItemsEnabled, setIsShowDebugItemsEnabled] = useState(() => {
    const stored = localStorage.getItem(SHOW_DEBUG_ITEMS_ENABLED);
    return stored !== null ? stored === "true" : false;
  });

  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Only show if the feature flag is enabled
  const isFeatureEnabled = import.meta.env.VITE_SHOW_DEBUG_ITEMS === "true";

  useEffect(() => {
    // If the feature flag is disabled, set the toggle to false and save to localStorage
    if (!isFeatureEnabled) {
      setIsShowDebugItemsEnabled(false);
      localStorage.setItem(SHOW_DEBUG_ITEMS_ENABLED, "false");
    }
  }, [isFeatureEnabled]);

  const handleMouseEnter = () => {
    if (!isFeatureEnabled) return;

    // Clear any existing hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    // Set a timeout to show the toggle after 2 seconds
    hoverTimeoutRef.current = setTimeout(() => {
      setShowToggle(true);
    }, 2000);
  };

  const handleMouseLeave = () => {
    if (!isFeatureEnabled) return;

    // Clear the hover timeout if it exists
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    // Hide the toggle after a short delay to allow interaction
    hideTimeoutRef.current = setTimeout(() => {
      setShowToggle(false);
    }, 500);
  };

  const handleToggleChange = () => {
    const newValue = !isShowDebugItemsEnabled;
    setIsShowDebugItemsEnabled(newValue);

    localStorage.setItem(SHOW_DEBUG_ITEMS_ENABLED, newValue.toString());

    // Dispatch custom event for same-tab changes
    window.dispatchEvent(new CustomEvent("debugItemsToggled"));
  };

  const handleToggleMouseEnter = () => {
    // Clear hide timeout when hovering over the toggle itself
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleToggleMouseLeave = () => {
    // Start hide timeout when leaving the toggle
    hideTimeoutRef.current = setTimeout(() => {
      setShowToggle(false);
    }, 500);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  if (!isFeatureEnabled) {
    return null;
  }

  return (
    <>
      {/* Hover trigger area in bottom right */}
      <div className="fixed right-0 bottom-0 z-30 h-16 w-16" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />

      {/* Toggle component */}
      {showToggle && (
        <div
          className="fixed right-6 bottom-6 z-40 min-w-[240px] rounded-lg border border-gray-200 bg-white p-4 shadow-lg"
          onMouseEnter={handleToggleMouseEnter}
          onMouseLeave={handleToggleMouseLeave}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Bug className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium text-gray-700">Show Debug Items</span>
            </div>
            <div className="flex items-center">
              <Switch checked={isShowDebugItemsEnabled} onCheckedChange={handleToggleChange} aria-label="Enable Show Debug Items" />
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {isShowDebugItemsEnabled ? "Debug items are currently visible" : "Debug items are currently hidden"}
          </p>
        </div>
      )}
    </>
  );
};
